import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result, Card, Typography } from 'antd';
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { weChatOAuthCallback } from '../../services/wechat';
import { saveAuthData, setUserInfo } from '../../utils/auth';
import { UserRole } from '../../constants/user';

const { Title, Text } = Typography;

const WeChatCallback: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state') || 'STATE';

        if (!code) {
          throw new Error('未获取到授权码');
        }

        // 调用后端处理OAuth回调
        const response = await weChatOAuthCallback(code, state);

        if (response.success && response.access_token) {
          // 保存认证数据
          saveAuthData({
            access_token: response.access_token,
            refresh_token: response.refresh_token || response.access_token,
            token_type: response.token_type || 'bearer',
          });

          // 保存用户信息 - 转换为UserInfo格式
          if (response.user_info) {
            // 将后端返回的角色字符串映射到UserRole枚举
            const mapRole = (roleStr: string): UserRole => {
              const roleMap: Record<string, UserRole> = {
                'ADMIN': UserRole.ADMIN,
                'FINANCE': UserRole.FINANCE,
                'LAWYER': UserRole.LAWYER,
                'STAFF': UserRole.STAFF,
                'admin': UserRole.ADMIN,
                'finance': UserRole.FINANCE,
                'lawyer': UserRole.LAWYER,
                'staff': UserRole.STAFF
              };
              return roleMap[roleStr] || UserRole.STAFF;
            };

            setUserInfo({
              id: response.user_info.id,
              username: response.user_info.username,
              email: response.user_info.email,
              name: response.user_info.real_name, // 将real_name映射到name
              role: mapRole(response.user_info.role),
              avatar: response.user_info.wechat_avatar,
              permissions: [], // 企业微信登录时暂时设为空，后续可以从后端获取
            });
          }

          setSuccess(true);
          setLoading(false);

          // 获取登录前的页面路径（从 state 或 localStorage）
          const returnUrl = localStorage.getItem('wechat_return_url') || '/';
          localStorage.removeItem('wechat_return_url');

          // 2秒后跳转到原始页面或主页面
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 2000);
        } else {
          throw new Error(response.message || '企业微信登录失败');
        }
      } catch (error: any) {
        console.error('WeChat OAuth callback error:', error);
        setError(error.message || '企业微信登录处理失败');
        setLoading(false);

        // 3秒后跳转到登录页面
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="login-container">
        <Card className="login-box">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
              size="large"
            />
            <Title level={3} style={{ marginTop: 24, color: '#1890ff' }}>
              正在处理企业微信登录...
            </Title>
            <Text type="secondary">
              请稍候，系统正在验证您的身份信息
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="login-container">
        <Card className="login-box">
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="企业微信登录成功！"
            subTitle="正在跳转到系统主页面..."
            extra={
              <Text type="secondary">
                如果页面没有自动跳转，请点击下方链接
              </Text>
            }
          />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-container">
        <Card className="login-box">
          <Result
            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
            title="企业微信登录失败"
            subTitle={error}
            extra={
              <Text type="secondary">
                3秒后自动返回登录页面...
              </Text>
            }
          />
        </Card>
      </div>
    );
  }

  return null;
};

export default WeChatCallback;